/* eslint-disable */
// src/lib/api.js — ClearFund API client
// Connects React frontend to Railway backend at API_URL

const API_URL = process.env.REACT_APP_API_URL || 'https://clearfund-backend-production.up.railway.app';

// ── helpers ──────────────────────────────────────────────────────────────────
function getToken() {
  return localStorage.getItem('cf_token');
}

async function req(method, path, body) {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 401) {
    // Token expired — try refresh
    try {
      const refreshToken = localStorage.getItem('cf_refresh_token');
      if (refreshToken) {
        const r = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (r.ok) {
          const data = await r.json();
          localStorage.setItem('cf_token', data.token || data.accessToken);
          // Retry original request with new token
          const retryRes = await fetch(`${API_URL}${path}`, {
            method,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${data.token || data.accessToken}`,
            },
            ...(body ? { body: JSON.stringify(body) } : {}),
          });
          if (!retryRes.ok) throw new Error(`${retryRes.status}`);
          return retryRes.json();
        }
      }
    } catch {}
    throw new Error('401');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `${res.status}`);
  }

  return res.json();
}

// ── auth ─────────────────────────────────────────────────────────────────────
export const auth = {
  login: (email, password) =>
    req('POST', '/auth/login', { email, password }).then(data => {
      if (data.token || data.accessToken) {
        localStorage.setItem('cf_token', data.token || data.accessToken);
      }
      if (data.refreshToken) {
        localStorage.setItem('cf_refresh_token', data.refreshToken);
      }
      if (data.user) {
        localStorage.setItem('cf_user', JSON.stringify(data.user));
      }
      return data;
    }),

  register: (body) =>
    req('POST', '/auth/register', body).then(data => {
      if (data.token || data.accessToken) {
        localStorage.setItem('cf_token', data.token || data.accessToken);
      }
      if (data.refreshToken) {
        localStorage.setItem('cf_refresh_token', data.refreshToken);
      }
      if (data.user) {
        localStorage.setItem('cf_user', JSON.stringify(data.user));
      }
      return data;
    }),

  logout: () => {
    localStorage.removeItem('cf_token');
    localStorage.removeItem('cf_refresh_token');
    localStorage.removeItem('cf_user');
    return req('POST', '/auth/logout').catch(() => {});
  },

  me: () => req('GET', '/auth/me'),

  refreshToken: () => {
    const refreshToken = localStorage.getItem('cf_refresh_token');
    return req('POST', '/auth/refresh', { refreshToken }).then(data => {
      if (data.token || data.accessToken) {
        localStorage.setItem('cf_token', data.token || data.accessToken);
      }
      return data;
    });
  },

  // Read stored user from localStorage — used by App.js at startup to restore session
  getStoredUser: () => {
    try {
      const raw = localStorage.getItem('cf_user');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },

  // Store user object in localStorage — call after login/register
  setStoredUser: (user) => {
    try {
      if (user) localStorage.setItem('cf_user', JSON.stringify(user));
      else localStorage.removeItem('cf_user');
    } catch {}
  },

  // Check if a token exists (user is logged in)
  isLoggedIn: () => !!localStorage.getItem('cf_token'),
};

// ── accounts ─────────────────────────────────────────────────────────────────
export const accounts = {
  // List all accounts for the logged-in user
  list: () => req('GET', '/accounts'),

  // Get a single account by ID — includes live balance, dailyPnl, drawdownUsed
  get: (id) => req('GET', `/accounts/${id}`),

  // Get account rule state (profit target progress, drawdown, etc.)
  getRules: (id) => req('GET', `/accounts/${id}/rules`),

  // Get daily snapshots for an account
  getSnapshots: (id, days = 30) => req('GET', `/accounts/${id}/snapshots?days=${days}`),
};

// ── trades ───────────────────────────────────────────────────────────────────
export const trades = {
  // Open a new position
  open: ({ accountId, symbol, side, quantity, entryPrice, stopLoss, takeProfit }) =>
    req('POST', '/trades', { accountId, symbol, side, quantity, entryPrice, stopLoss, takeProfit }),

  // Close a position by trade ID
  close: (tradeId, exitPrice) =>
    req('PATCH', `/trades/${tradeId}/close`, { exitPrice }),

  // Close all positions for an account
  closeAll: (accountId, exitPrice) =>
    req('POST', `/trades/close-all`, { accountId, exitPrice }),

  // Get open positions for an account
  getPositions: (accountId) =>
    req('GET', `/trades?accountId=${accountId}&status=OPEN`).then(res => {
      // Handle both { trades: [...] } and [...] shapes
      return Array.isArray(res) ? res : (res?.trades ?? res?.positions ?? []);
    }),

  // List trades with filters
  list: ({ accountId, status, limit = 50, offset = 0 } = {}) => {
    const params = new URLSearchParams();
    if (accountId) params.set('accountId', accountId);
    if (status) params.set('status', status);
    params.set('limit', limit);
    params.set('offset', offset);
    return req('GET', `/trades?${params}`);
  },

  // Add journal note to a trade
  addNote: (tradeId, note) =>
    req('PATCH', `/trades/${tradeId}/note`, { note }),
};

// ── payments ─────────────────────────────────────────────────────────────────
export const payments = {
  // Create a Stripe checkout session
  createCheckout: ({ plan, size, couponCode }) =>
    req('POST', '/stripe/create-checkout', { plan, size, couponCode }),

  // Get payout history
  getPayouts: (accountId) =>
    req('GET', `/payouts?accountId=${accountId}`),

  // Request a payout
  requestPayout: ({ accountId, amount }) =>
    req('POST', '/payouts/request', { accountId, amount }),
};

// ── admin ─────────────────────────────────────────────────────────────────────
export const admin = {
  getUsers: (params = {}) => {
    const q = new URLSearchParams(params);
    return req('GET', `/admin/users?${q}`);
  },
  getUser: (id) => req('GET', `/admin/users/${id}`),
  updateAccount: (id, body) => req('PATCH', `/admin/accounts/${id}`, body),
  getPayouts: () => req('GET', '/admin/payouts'),
  approvePayour: (id) => req('PATCH', `/admin/payouts/${id}/approve`),
  rejectPayout: (id, reason) => req('PATCH', `/admin/payouts/${id}/reject`, { reason }),
  getDashboard: () => req('GET', '/admin/dashboard'),
  getRiskMonitor: () => req('GET', '/admin/risk'),
};
